// AirportDetail — ForeFlight-style airport detail panel.
//
// Layout responsiveness:
//   - Mobile (< md): bottom sheet that covers ~60% of the screen.
//   - Tablet / desktop (>= md): right-side panel ~30% (380px on tablet,
//     420px on desktop).
//
// Information architecture (mirroring ForeFlight):
//   Header
//     • Identifier + name
//     • Location (city, state)
//     • Sunrise / sunset (auto-refreshed for current calendar day)
//     • Field elevation
//     • Longest runway summary (e.g. "7,490' x 150' asphalt")
//   Tabs (level 1):
//     1. Info       — chart-supplement-style frequencies + phones
//     2. Weather    — sub-tabs: METAR / TAF / MOS / Daily / Winds
//     3. Runway     — list of runways; tap a row for full detail
//     4. Procedure  — sub-tabs: Airport / Departure / Approach
//     5. NOTAMs     — sub-tabs: Airport / Obstacle / TFR-ARTCC
//
// V1 coverage:
//   - Info, Runway, METAR + TAF (live), sunrise/sunset (live) are wired.
//   - MOS, Daily, Winds, Procedure tabs, NOTAMs tabs are stubbed with
//     a clear "Source: <FAA d-CS / FAA d-TPP / FAA NOTAM API>" note so
//     follow-on work knows exactly where to plug in. See
//     docs/airport-detail-spec.md.

import { useEffect, useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import { Icon } from '../shell/Icon';
import type { AirportDetail as ApiAirportDetail, Runway } from '../lib/airports';
import { getAirportDetail } from '../lib/airports';
import { useBreakpoint } from '../shell/useBreakpoint';
import { InfoTab } from './tabs/InfoTab';
import { RunwayTab } from './tabs/RunwayTab';
import { WeatherTab } from './tabs/WeatherTab';
import { LiveTab } from './tabs/LiveTab';
import { ProcedureTab } from './tabs/ProcedureTab';
import { NotamTab } from './tabs/NotamTab';
import { Sun, Sunset, Mountain, Plane, Map as MapIcon } from '../shell/Icon';
import { fetchAirportPlates, type PlateRecord } from '../lib/plates';
import { PlateViewer } from '../plates/PlateViewer';
import { formatLocalTime } from '../lib/timeFormat';
import InsertIntoRouteButtons from '../components/route/InsertIntoRouteButtons';

import { usePlanStore } from '../stores/planStore';
import { useUiStore } from '../stores/uiStore';

export type AirportTabKey = 'info' | 'weather' | 'live' | 'runway' | 'procedure' | 'notam';

const TABS: Array<{ key: AirportTabKey; label: string }> = [
  { key: 'info', label: 'Info' },
  { key: 'weather', label: 'Weather' },
  { key: 'live', label: 'Live' },
  { key: 'runway', label: 'Runway' },
  { key: 'procedure', label: 'Procedure' },
  { key: 'notam', label: 'NOTAMs' },
];

interface Props {
  ident: string;
  /** Initial coords used while the detail fetch is in flight. */
  initialLat?: number;
  initialLon?: number;
  initialName?: string;
  onClose: () => void;
}

interface SunInfo {
  sunriseLocal: string; // HH:mm in viewer local
  sunsetLocal: string;
}

export default function AirportDetail({ ident, initialLat, initialLon, initialName, onClose }: Props) {
  const isMobile = !useBreakpoint('md');

  // Reactive deep-link: subscribe to pendingPlateOpen in the ui store so we
  // handle BOTH the first-mount case (AirportsPage sets it before navigating
  // here) AND the already-mounted case (user had KMRY open, clicks Fix again
  // — AirportDetail doesn't remount, so the old ref-on-mount approach dropped
  // the deep-link silently). See: docs/plate-deeplink-race-fix.md.
  //
  // Synchronous read in useState initializers avoids the info→procedure
  // flash on first mount; the reactive useEffect covers subsequent arrivals.
  const pendingPlateOpen = useUiStore((s) => s.pendingPlateOpen);
  const setPendingPlateOpen = useUiStore((s) => s.setPendingPlateOpen);

  const [activePlateOpen, setActivePlateOpen] = useState<{ pdfName: string; autoEditor: boolean } | null>(() => {
    // Read synchronously so tab initializer below sees the same value.
    return useUiStore.getState().pendingPlateOpen;
  });
  const [tab, setTab] = useState<AirportTabKey>(() => {
    return useUiStore.getState().pendingPlateOpen ? 'procedure' : 'info';
  });

  // Reactive effect: fires whenever pendingPlateOpen is set (covers the
  // already-mounted scenario where useState initializers don't re-run).
  useEffect(() => {
    if (!pendingPlateOpen) return;
    setActivePlateOpen(pendingPlateOpen);
    setTab('procedure');
    setPendingPlateOpen(null);
  }, [pendingPlateOpen, setPendingPlateOpen]);
  const [sun, setSun] = useState<SunInfo | null>(null);
  // Airport diagram metadata pulled from d-TPP. We surface a small
  // 'Diagram' button in the header so the user can pop the full plate
  // viewer without having to drill into the Procedure tab. Boss
  // feedback 2026-05-10: 'put a clickable airport map in the header
  // of every airport detail card'.
  const [diagram, setDiagram] = useState<{ cycle: string; plate: PlateRecord } | null>(null);
  const [diagramOpen, setDiagramOpen] = useState(false);

  // Fetch full detail via the existing airports lib (which does its own
  // IDB caching, so repeat opens are instant).
  const { data, loading, error: fetchError } = useFetch(
    () => getAirportDetail(ident).then((d) => {
      if (!d) throw new Error('airport not found');
      return d;
    }),
    [ident],
  );
  const error = fetchError != null ? (fetchError instanceof Error ? fetchError.message : 'fetch failed') : null;

  // Pull the d-TPP plate list and stash the first 'APD' (Airport
  // Diagram) plate. Most non-towered fields don't have one, but
  // every towered field does — including military / joint-use
  // fields where the APD is published as `civil: false` (e.g. Travis
  // AFB, Edwards). Boss feedback 2026-05-10: 'I want it available in
  // the header for all airports that have a diagram.' We previously
  // filtered to `civil: true`, which hid the diagram for towered
  // joint-use fields where civilian ops do happen. Drop that filter
  // — an APD is purely geographic (runway layout); the civil flag
  // tracks procedural plates and doesn't apply here.
  //
  // Some airports publish multiple APDs (e.g. Edwards has separate
  // diagrams for the main field and the lakebeds). We pick the first
  // one whose name doesn't carry a parenthetical sub-area marker so
  // the button always points at the canonical airfield diagram.
  useEffect(() => {
    let cancelled = false;
    setDiagram(null);
    const ac = new AbortController();
    fetchAirportPlates(ident, ac.signal)
      .then((d) => {
        if (cancelled) return;
        const apds = (d.plates || []).filter((p) => p.chartCode === 'APD');
        // Prefer a plate whose chartName is just 'AIRPORT DIAGRAM'
        // (no '(LAKEBED)' / '(NORTH FIELD)' qualifier). Fall back to
        // the first APD if every entry is qualified.
        const plate =
          apds.find((p) => !/\(/.test(p.chartName)) || apds[0];
        if (plate) setDiagram({ cycle: d.cycle, plate });
      })
      .catch(() => undefined);
    return () => { cancelled = true; ac.abort(); };
  }, [ident]);

  // Sunrise/sunset for *today* at the airport coords. We use whichever
  // coords come first: the live detail's lat/lon, or the initial hint
  // from the search hit. Either way the value drives the header chip.
  useEffect(() => {
    const lat = data?.airport.lat ?? initialLat;
    const lon = data?.airport.lon ?? initialLon;
    if (lat == null || lon == null) return;
    const ac = new AbortController();
    fetch(`/api/sun?lat=${lat}&lon=${lon}`, { signal: ac.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then((j) => {
        const r = j.results;
        if (!r) return;
        // Sunrise / sunset rendered in the user's preferred clock
        // format. 24h is the right default for pilots, but the helper
        // honors the global setting so AM/PM still works if the
        // setting is off.
        setSun({
          sunriseLocal: formatLocalTime(r.sunrise),
          sunsetLocal: formatLocalTime(r.sunset),
        });
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, [data?.airport.lat, data?.airport.lon, initialLat, initialLon]);

  const ap = data?.airport;
  const containerClass = isMobile
    ? // Bottom sheet, ~60% of viewport, slide-up.
      'absolute inset-x-0 bottom-0 z-40 max-h-[65vh] h-[60vh] bg-cockpit-800/98 backdrop-blur border-t border-cockpit-600 rounded-t-2xl shadow-bar flex flex-col'
    : // Right-side panel, ~30% / 420px, full height.
      'absolute z-40 right-0 bottom-0 w-[22rem] md:w-[26rem] xl:w-[30rem] bg-cockpit-800/98 backdrop-blur border-l border-cockpit-600 shadow-rail flex flex-col';

  // Top offset on tablet/desktop: clear the topbar (~3.5rem) and respect safe area.
  const containerStyle = isMobile
    ? { paddingBottom: 'var(--safe-bottom)' }
    : { top: 'calc(var(--safe-top) + 3.5rem)' };

  return (
    <aside
      className={containerClass}
      style={containerStyle}
      role="dialog"
      aria-label={`${ident} airport detail`}
    >
      {/* Drag handle on mobile */}
      {isMobile && (
        <div className="flex items-center justify-center pt-2 pb-1 shrink-0">
          <span className="block h-1 w-10 rounded-full bg-cockpit-500" aria-hidden="true" />
        </div>
      )}

      {/* Header */}
      <header className="px-4 pt-2 pb-3 border-b border-cockpit-700 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="text-2xl font-mono font-semibold text-cockpit-100 leading-tight">
                {ap?.ident || ident}
              </h2>
              {ap?.iata && ap.iata !== ap.ident && (
                <span className="text-xs font-mono text-cockpit-300">{ap.iata}</span>
              )}
            </div>
            <div className="text-sm text-cockpit-200 truncate">
              {ap?.name || initialName || (loading ? 'Loading…' : '—')}
            </div>
            <div className="text-xs text-cockpit-400">
              {ap ? [ap.city, ap.iso_region?.replace('US-', '')].filter(Boolean).join(', ') : ''}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {diagram && (
              <button
                onClick={() => setDiagramOpen(true)}
                className="flex items-center gap-1.5 px-2.5 h-8 rounded-md bg-cockpit-700 hover:bg-cockpit-600 active:bg-cockpit-500 border border-cockpit-600 text-[11px] text-cockpit-100 transition-colors"
                aria-label="Open airport diagram"
                title="Airport Diagram"
              >
                <MapIcon className="h-3.5 w-3.5" strokeWidth={2} />
                <span>Diagram</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="text-cockpit-300 hover:text-cockpit-100 h-8 w-8 flex items-center justify-center rounded shrink-0"
              aria-label="Close airport detail"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Add to flight plan — or, when this airport is already a
            route waypoint, offer to add a hold at it instead. Mirrors
            the DetailPane plan-aware action surfaced 2026-05-17. */}
        <AirportPlanActions
          ident={ap?.ident ?? ident}
          lat={ap?.lat ?? initialLat ?? 0}
          lon={ap?.lon ?? initialLon ?? 0}
        />

        {/* Quick-fact chips — 6-col grid.
            Row 1: SR | SS | ELEV   (each col-span-2)
            Row 2: RWY (clickable, cycles)  |  TPA (clickable, cycles aircraft cat)
                   each col-span-3 so they're each ~50 % wide. Wider pills mean
              the boss can actually read the values + designators. (Boss
              2026-05-21 19:26 PDT.) */}
        <div className="mt-3 grid grid-cols-6 gap-2 text-xs">
          <Fact className="col-span-2" icon={<Sun className="h-3.5 w-3.5" strokeWidth={1.8} />} label="SR" value={sun?.sunriseLocal ?? '—'} />
          <Fact className="col-span-2" icon={<Sunset className="h-3.5 w-3.5" strokeWidth={1.8} />} label="SS" value={sun?.sunsetLocal ?? '—'} />
          <Fact className="col-span-2" icon={<Mountain className="h-3.5 w-3.5" strokeWidth={1.8} />} label="ELEV" value={ap?.elev != null ? `${ap.elev.toLocaleString()}'` : '—'} />
          <RwyFact className="col-span-3" runways={data?.runways ?? []} />
          <TpaFact className="col-span-3" airportElevFt={ap?.elev ?? null} />
        </div>
      </header>

      {/* Top tabs */}
      <nav role="tablist" className="flex border-b border-cockpit-700 shrink-0 overflow-x-auto scroll-touch">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={[
              'flex-1 min-w-[5rem] text-xs font-medium py-2.5 px-3 transition-colors whitespace-nowrap',
              tab === t.key
                ? 'text-ember-400 border-b-2 border-ember-500'
                : 'text-cockpit-300 hover:text-cockpit-100 border-b-2 border-transparent',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto scroll-touch">
        {error ? (
          <div className="p-4 text-sm text-red-400">⚠ {error}</div>
        ) : loading ? (
          <div className="p-4 text-sm text-cockpit-300">Loading…</div>
        ) : data ? (
          <>
            {tab === 'info' && <InfoTab detail={data} />}
            {tab === 'weather' && (
              <WeatherTab
                ident={ident}
                lat={data?.airport.lat ?? initialLat}
                lon={data?.airport.lon ?? initialLon}
              />
            )}
            {tab === 'runway' && <RunwayTab detail={data} />}
            {tab === 'procedure' && (
              <ProcedureTab
                ident={ident}
                openPdfName={activePlateOpen?.pdfName}
                autoEditor={activePlateOpen?.autoEditor}
                airportElevFt={data?.airport.elev ?? 0}
              />
            )}
            {tab === 'live' && <LiveTab ident={ident} />}
            {tab === 'notam' && <NotamTab ident={ident} />}
          </>
        ) : null}
      </div>

      {/* Fullscreen airport-diagram viewer when the header 'Diagram'
          button is tapped. Mounted here (inside the panel) so it
          inherits z-index above other detail surfaces but stays
          dismissable independently of the rest of the airport panel. */}
      {diagram && diagramOpen && (
        <PlateViewer
          cycle={diagram.cycle}
          pdfName={diagram.plate.pdfName}
          title={`${ident} · Airport Diagram`}
          subtitle={ap?.name || initialName}
          onClose={() => setDiagramOpen(false)}
        />
      )}
    </aside>
  );
}

function Fact({
  icon, label, value, noTruncate, className = '',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  noTruncate?: boolean;
  className?: string;
}) {
  return (
    <div className={`bg-cockpit-700/50 border border-cockpit-700 rounded-md px-2 py-1.5 flex items-center gap-1.5 min-w-0 overflow-hidden ${className}`}>
      <span className="text-cockpit-300 shrink-0">{icon}</span>
      <span className="text-[9px] tracking-widest text-cockpit-400 shrink-0">{label}</span>
      <span className={[
        'font-mono text-cockpit-100 min-w-0',
        noTruncate ? 'whitespace-nowrap' : 'truncate',
      ].join(' ')}>{value}</span>
    </div>
  );
}

/** Traffic Pattern Altitude pill with category selector.
 *  TPA = airport elevation + standard AGL offset per AIM 4-3-3:
 *    Single-engine piston       1,000 ft AGL  (default — most GA)
 *    Jet / Turbine / Heavy      1,500 ft AGL
 *    Helicopter                   500 ft AGL
 *    Glider                       800 ft AGL
 *    Ultralight                   500 ft AGL
 *  Click the pill to cycle categories; persists choice in component state.
 */
function TpaFact({ airportElevFt, className = '' }: { airportElevFt: number | null; className?: string }) {
  const CATS = [
    { key: 'piston',  label: 'PSTN', aglFt: 1000, hint: 'Piston (1000 AGL)' },
    { key: 'jet',     label: 'JET',  aglFt: 1500, hint: 'Jet/Turbine (1500 AGL)' },
    { key: 'helo',    label: 'HELO', aglFt:  500, hint: 'Helicopter (500 AGL)' },
    { key: 'glider',  label: 'GLDR', aglFt:  800, hint: 'Glider (800 AGL)' },
    { key: 'ulv',     label: 'ULV',  aglFt:  500, hint: 'Ultralight (500 AGL)' },
  ] as const;
  const [catIdx, setCatIdx] = useState(0);
  const cat = CATS[catIdx];
  const tpaMsl = airportElevFt != null ? Math.round((airportElevFt + cat.aglFt) / 50) * 50 : null;
  const cycle = () => setCatIdx((i) => (i + 1) % CATS.length);
  return (
    <button
      type="button"
      onClick={cycle}
      title={`${cat.hint} — tap to switch category`}
      className={`bg-cockpit-700/50 border border-cockpit-700 rounded-md px-2 py-1.5 flex items-center gap-1.5 min-w-0 overflow-hidden hover:bg-cockpit-700 hover:border-ember-500 transition-colors text-left ${className}`}
    >
      <Plane className="h-3.5 w-3.5 text-cockpit-300 shrink-0" strokeWidth={1.8} />
      <span className="text-[9px] tracking-widest text-cockpit-400 shrink-0">TPA · {cat.label}</span>
      <span className="font-mono text-cockpit-100 whitespace-nowrap min-w-0">
        {tpaMsl != null ? `${tpaMsl.toLocaleString()}'` : '—'}
      </span>
    </button>
  );
}

/** Runway pill — cycles through the airport's open runways on tap, showing
 *  designator (e.g. '09/27') and length. Sorted longest first so the
 *  default display is the longest runway (matches previous behavior).
 *  Boss request 2026-05-21 19:26 PDT. */
function RwyFact({ runways, className = '' }: { runways: readonly Runway[]; className?: string }) {
  // Filter out closed runways and any without a usable length.
  const usable = runways
    .filter((r) => !r.closed && r.length_ft != null)
    .sort((a, b) => (b.length_ft ?? 0) - (a.length_ft ?? 0));
  const [idx, setIdx] = useState(0);
  // Reset to 0 when the airport changes (runways array reference changes).
  // useState's initial doesn't re-run, so guard if idx is out of bounds.
  const safeIdx = idx >= usable.length ? 0 : idx;
  const rwy = usable[safeIdx];
  const total = usable.length;
  const cycle = () => setIdx((i) => (i + 1) % Math.max(usable.length, 1));

  // No usable runways — keep the look but disable interaction.
  if (!rwy) {
    return (
      <div className={`bg-cockpit-700/50 border border-cockpit-700 rounded-md px-2 py-1.5 flex items-center gap-1.5 min-w-0 overflow-hidden ${className}`}>
        <Plane className="h-3.5 w-3.5 text-cockpit-300 shrink-0" strokeWidth={1.8} />
        <span className="text-[9px] tracking-widest text-cockpit-400 shrink-0">RWY</span>
        <span className="font-mono text-cockpit-400 whitespace-nowrap min-w-0">—</span>
      </div>
    );
  }

  const designator = `${rwy.le_ident}/${rwy.he_ident}`;
  const len = rwy.length_ft!.toLocaleString();
  const hint = total > 1
    ? `Runway ${safeIdx + 1} of ${total} — tap to cycle`
    : `Sole runway`;

  return (
    <button
      type="button"
      onClick={cycle}
      disabled={total <= 1}
      title={hint}
      className={`bg-cockpit-700/50 border border-cockpit-700 rounded-md px-2 py-1.5 flex items-center gap-1.5 min-w-0 overflow-hidden ${total > 1 ? 'hover:bg-cockpit-700 hover:border-ember-500 transition-colors cursor-pointer' : 'cursor-default'} text-left ${className}`}
    >
      <Plane className="h-3.5 w-3.5 text-cockpit-300 shrink-0" strokeWidth={1.8} />
      <span className="text-[9px] tracking-widest text-cockpit-400 shrink-0">RWY</span>
      <span className="font-mono text-cockpit-100 whitespace-nowrap min-w-0">
        {designator} · {len}’
      </span>
      {total > 1 && (
        <span className="text-[9px] text-cockpit-400 shrink-0 ml-auto">{safeIdx + 1}/{total}</span>
      )}
    </button>
  );
}

/** Plan-aware action button for AirportDetail. Renders "Add to route"
 *  buttons when the airport is NOT already in the active route; otherwise
 *  renders nothing (the "Add hold at this airport" button was removed
 *  2026-05-21 per boss — holds are added from the route detail panel
 *  instead, this button cluttered the airport header). */
function AirportPlanActions({
  ident, lat, lon,
}: { ident: string; lat: number; lon: number }) {
  const route = usePlanStore((s) => s.route);
  const inPlan = route.some((p) =>
    p.ident === ident
    && Math.abs(p.lat - lat) < 0.0001
    && Math.abs(p.lon - lon) < 0.0001,
  );
  if (inPlan) return null;
  return (
    <InsertIntoRouteButtons
      point={{ ident, lat, lon, kind: 'airport' }}
      className="mt-2"
      compact
    />
  );
}
